import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  generatePackages,
  analyzeFile,
  suggestPricing,
  applyPackagesToService,
  selectPackages,
  selectFileAnalysis,
  selectPricingSuggestions,
} from "../../store/slices/aiSlice";
import {
  PenTool, Upload, TrendingUp, Loader2, Check, X, IndianRupee, Package,
  Pencil, Trash2, Plus, Save,
} from "lucide-react";
import { ListSkeleton } from "../UI/Skeleton";
import toast from "react-hot-toast";
import api from "../../utils/axios";
import PackageQuestionnaire from "./PackageQuestionnaire";
import { includeGroups, excludeGroups } from "../../data/packageTaxonomy";

const tierColors = {
  basic: "border-gray-300 bg-gray-50",
  standard: "border-purple-300 bg-purple-50",
  premium: "border-purple-300 bg-purple-50",
  custom: "border-amber-300 bg-amber-50",
};

const TIERS = ["basic", "standard", "premium", "custom"];

const emptyPackage = () => ({
  name: "",
  description: "",
  price: 0,
  tier: "standard",
  includes: [],
  excludes: [],
});

/* ── Read-only card (used for freshly generated / suggested results) ───────── */
function PackageCard({ pkg }) {
  const color = tierColors[pkg.tier] || tierColors.standard;
  return (
    <div className={`border rounded-xl p-4 ${color}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-gray-800 text-sm">{pkg.name || pkg.packageName}</h4>
        <span className="text-xs font-medium uppercase px-2 py-0.5 rounded-full bg-white border">
          {pkg.tier || pkg.type || "—"}
        </span>
      </div>
      {pkg.description && <p className="text-xs text-gray-600 mb-3">{pkg.description}</p>}
      <div className="flex items-center gap-1 text-lg font-bold text-gray-900 mb-3">
        <IndianRupee size={16} />
        {(pkg.price || 0).toLocaleString("en-IN")}
      </div>
      {pkg.includes?.length > 0 && (
        <div className="mb-2">
          <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Includes</p>
          <ul className="space-y-0.5">
            {pkg.includes.map((item, i) => (
              <li key={i} className="text-xs text-gray-700 flex items-center gap-1">
                <Check size={10} className="text-emerald-500" /> {item}
              </li>
            ))}
          </ul>
        </div>
      )}
      {pkg.excludes?.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Excludes</p>
          <ul className="space-y-0.5">
            {pkg.excludes.map((item, i) => (
              <li key={i} className="text-xs text-gray-400 flex items-center gap-1">
                <X size={10} className="text-rose-400" /> {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ── Curated tag suggestion chips (from the package taxonomy) ───────────────── */
function TagSuggestions({ groups, existing, onPick }) {
  const [open, setOpen] = useState(false);
  const cats = Object.keys(groups || {});
  if (cats.length === 0) return null;

  const has = (t) => (existing || []).some((e) => e.toLowerCase() === t.toLowerCase());

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[10px] font-semibold text-purple-600 hover:text-purple-800"
      >
        {open ? "Hide suggestions" : "＋ Suggested tags"}
      </button>
      {open && (
        <div className="mt-1.5 space-y-2 max-h-40 overflow-y-auto pr-1">
          {cats.map((cat) => {
            const avail = groups[cat].filter((t) => !has(t));
            if (avail.length === 0) return null;
            return (
              <div key={cat}>
                <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide mb-1">{cat}</p>
                <div className="flex flex-wrap gap-1">
                  {avail.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => onPick(t)}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-purple-50 border border-purple-200 text-purple-700 hover:bg-purple-100"
                    >
                      + {t}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Editable line-item list (includes / excludes) ─────────────────────────── */
function LineItems({ label, items, tone, onChange, serviceType }) {
  const [draft, setDraft] = useState("");
  const add = (val) => {
    const v = (val ?? draft).trim();
    if (!v) return;
    if ((items || []).some((e) => e.toLowerCase() === v.toLowerCase())) return;
    onChange([...(items || []), v]);
    setDraft("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const groups = tone === "include" ? includeGroups(serviceType) : excludeGroups(serviceType);
  return (
    <div>
      <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">{label}</p>
      <ul className="space-y-1 mb-1.5">
        {(items || []).map((item, i) => (
          <li key={i} className="flex items-center gap-1 text-xs text-gray-700">
            {tone === "include" ? (
              <Check size={10} className="text-emerald-500 flex-shrink-0" />
            ) : (
              <X size={10} className="text-rose-400 flex-shrink-0" />
            )}
            <span className="flex-1">{item}</span>
            <button type="button" onClick={() => remove(i)} className="text-gray-300 hover:text-rose-500">
              <X size={11} />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-1">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={`Add ${label.toLowerCase()}…`}
          className="flex-1 px-2 py-1 text-xs bg-white border border-gray-200 rounded outline-none focus:ring-1 focus:ring-purple-300"
        />
        <button type="button" onClick={() => add()} className="px-1.5 rounded bg-gray-100 text-gray-500 hover:bg-gray-200">
          <Plus size={12} />
        </button>
      </div>
      <TagSuggestions groups={groups} existing={items} onPick={(t) => add(t)} />
    </div>
  );
}

/* ── Editable package card ─────────────────────────────────────────────────── */
function EditablePackageCard({ pkg, onChange, onDelete, serviceType }) {
  const color = tierColors[pkg.tier] || tierColors.standard;
  const patch = (p) => onChange({ ...pkg, ...p });
  return (
    <div className={`border rounded-xl p-4 ${color} space-y-3`}>
      <div className="flex items-start gap-2">
        <input
          value={pkg.name || ""}
          onChange={(e) => patch({ name: e.target.value })}
          placeholder="Package name"
          className="flex-1 px-2 py-1 text-sm font-semibold bg-white/80 border border-gray-200 rounded outline-none focus:ring-1 focus:ring-purple-300"
        />
        <button
          type="button"
          onClick={onDelete}
          title="Delete package"
          className="p-1.5 rounded-lg bg-white/80 text-rose-500 hover:bg-rose-50 border border-gray-200"
        >
          <Trash2 size={14} />
        </button>
      </div>

      <div className="flex gap-2">
        <select
          value={pkg.tier || "standard"}
          onChange={(e) => patch({ tier: e.target.value })}
          className="px-2 py-1 text-xs bg-white border border-gray-200 rounded outline-none focus:ring-1 focus:ring-purple-300 capitalize"
        >
          {TIERS.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <div className="flex items-center gap-1 flex-1 px-2 bg-white border border-gray-200 rounded">
          <IndianRupee size={13} className="text-gray-400" />
          <input
            type="number"
            min="0"
            value={pkg.price ?? 0}
            onChange={(e) => patch({ price: Number(e.target.value) })}
            className="w-full py-1 text-sm font-semibold outline-none bg-transparent"
          />
        </div>
      </div>

      <textarea
        value={pkg.description || ""}
        onChange={(e) => patch({ description: e.target.value })}
        placeholder="Short description"
        rows={2}
        className="w-full px-2 py-1 text-xs bg-white/80 border border-gray-200 rounded outline-none focus:ring-1 focus:ring-purple-300 resize-none"
      />

      <LineItems label="Includes" items={pkg.includes} tone="include" onChange={(v) => patch({ includes: v })} serviceType={serviceType} />
      <LineItems label="Excludes" items={pkg.excludes} tone="exclude" onChange={(v) => patch({ excludes: v })} serviceType={serviceType} />
    </div>
  );
}

export default function AIPackageBuilder({ serviceType, serviceData, serviceId, onApplyPackages }) {
  const dispatch = useDispatch();
  const packages = useSelector(selectPackages);
  const fileAnalysis = useSelector(selectFileAnalysis);
  const pricing = useSelector(selectPricingSuggestions);
  const fileRef = useRef();
  const [activeTab, setActiveTab] = useState("generate");
  const [savedPackages, setSavedPackages] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [questionnaire, setQuestionnaire] = useState({ tierCount: 3, mustHave: [] });

  // Fetch saved packages on mount / service change
  useEffect(() => {
    if (serviceId && serviceType) {
      setLoadingSaved(true);
      setEditing(false);
      setDirty(false);
      api.get(`/api/vendor/ai/service-packages/${serviceType}/${serviceId}`)
        .then(({ data }) => setSavedPackages(data?.data?.aiPackages || []))
        .catch(() => {})
        .finally(() => setLoadingSaved(false));
    }
  }, [serviceId, serviceType]);

  const handleGenerate = () => {
    if (!serviceType) return toast.error("Service type required");
    dispatch(generatePackages({ serviceType, serviceData, questionnaire }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    dispatch(analyzeFile({ file, serviceType }));
  };

  const handlePricing = () => {
    if (!serviceType) return toast.error("Service type required");
    dispatch(suggestPricing({ serviceType, serviceData }));
  };

  /* ── Saved-package editing helpers ───────────────────────────────────────── */
  const updateSaved = (index, next) => {
    setSavedPackages((list) => list.map((p, i) => (i === index ? next : p)));
    setDirty(true);
  };
  const deleteSaved = (index) => {
    setSavedPackages((list) => list.filter((_, i) => i !== index));
    setDirty(true);
  };
  const addSaved = () => {
    setSavedPackages((list) => [...list, emptyPackage()]);
    setDirty(true);
    setEditing(true);
  };

  const saveEdits = async () => {
    if (!serviceId) return;
    const valid = savedPackages.filter((p) => (p.name || "").trim());
    if (valid.length === 0) return toast.error("Add at least one named package");
    setSaving(true);
    try {
      // Package-only edit — do NOT overwrite the vendor's real pricing config.
      const result = await dispatch(
        applyPackagesToService({ serviceType, serviceId, packages: valid, updatePricing: false })
      ).unwrap();
      setSavedPackages(result?.aiPackages || valid);
      setDirty(false);
      setEditing(false);
      toast.success("Packages updated");
    } catch (err) {
      toast.error(err || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const currentPackages =
    activeTab === "generate" ? packages.data?.packages :
    activeTab === "upload" ? fileAnalysis.data?.packages :
    null;

  const isLoading =
    activeTab === "generate" ? packages.loading :
    activeTab === "upload" ? fileAnalysis.loading :
    pricing.loading;

  const tabs = [
    { key: "generate", label: "Auto-Generate", icon: PenTool },
    { key: "upload", label: "Upload File", icon: Upload },
    { key: "pricing", label: "Market Pricing", icon: TrendingUp },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-5 sm:p-6 space-y-6">
      {/* Saved packages — editable */}
      {(savedPackages.length > 0 || loadingSaved) && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Package size={15} className="text-purple-500" />
              Current Packages ({savedPackages.length})
            </h4>
            {serviceId && savedPackages.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={addSaved}
                  className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-purple-600"
                >
                  <Plus size={13} /> Add
                </button>
                <button
                  type="button"
                  onClick={() => setEditing((e) => !e)}
                  className={`flex items-center gap-1 text-xs font-medium ${
                    editing ? "text-purple-600" : "text-gray-600 hover:text-purple-600"
                  }`}
                >
                  <Pencil size={13} /> {editing ? "Done editing" : "Edit"}
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {savedPackages.map((pkg, i) =>
              editing ? (
                <EditablePackageCard
                  key={i}
                  pkg={pkg}
                  onChange={(next) => updateSaved(i, next)}
                  onDelete={() => deleteSaved(i)}
                  serviceType={serviceType}
                />
              ) : (
                <PackageCard key={i} pkg={pkg} />
              )
            )}
          </div>

          {/* Save bar */}
          {dirty && (
            <div className="mt-4 flex items-center justify-between bg-purple-50 border border-purple-200 rounded-xl px-4 py-2.5">
              <span className="text-xs text-purple-700 font-medium">You have unsaved changes</span>
              <button
                type="button"
                onClick={saveEdits}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-semibold shadow-md shadow-purple-600/20 hover:shadow-lg transition-all disabled:opacity-60"
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          )}
        </div>
      )}

      {loadingSaved && savedPackages.length === 0 && (
        <ListSkeleton rows={2} />
      )}

      <div className="border-t border-gray-100 pt-5">
        <h3 className="font-['Manrope'] text-base font-bold text-gray-900 mb-4 flex items-center gap-3">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-600/20"><PenTool size={17} className="text-white" /></span>
          AI Package Builder
        </h3>

        {/* Tabs */}
        <div className="inline-flex max-w-full gap-1 overflow-x-auto p-1 mb-5 rounded-xl border border-gray-200/70 bg-white">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all ${
                activeTab === key
                  ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {/* Action area */}
        {activeTab === "generate" && (
          <>
            <PackageQuestionnaire
              serviceType={serviceType}
              value={questionnaire}
              onChange={setQuestionnaire}
            />
            <button
              onClick={handleGenerate}
              disabled={isLoading}
              className="w-full mb-4 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold hover:opacity-90 transition flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {isLoading ? <Loader2 size={16} className="animate-spin" /> : <PenTool size={16} />}
              {isLoading ? "Generating..." : "Generate Packages"}
            </button>
          </>
        )}

        {activeTab === "upload" && (
          <div className="mb-4">
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center cursor-pointer hover:border-purple-300 hover:bg-purple-50/40 transition"
            >
              <Upload size={24} className="mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600">Upload rate card, menu, or brochure</p>
              <p className="text-xs text-gray-400 mt-1">PDF, TXT, or DOC</p>
            </div>
            <input ref={fileRef} type="file" className="hidden" accept=".pdf,.txt,.doc,.docx" onChange={handleFileUpload} />
            {fileAnalysis.loading && (
              <div className="mt-3 flex items-center gap-2 text-sm text-purple-600">
                <Loader2 size={14} className="animate-spin" /> Analyzing file...
              </div>
            )}
          </div>
        )}

        {activeTab === "pricing" && (
          <button
            onClick={handlePricing}
            disabled={pricing.loading}
            className="w-full mb-4 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold hover:opacity-90 transition flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {pricing.loading ? <Loader2 size={16} className="animate-spin" /> : <TrendingUp size={16} />}
            {pricing.loading ? "Researching..." : "Get Market Pricing"}
          </button>
        )}

        {/* Pricing tiers result */}
        {activeTab === "pricing" && pricing.data?.tiers && (
          <div className="space-y-2 mb-4">
            {pricing.data.tiers.map((tier, i) => (
              <div key={i} className="rounded-xl border border-gray-200/70 bg-white p-3.5">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm text-gray-800 capitalize">{tier.tier}</span>
                  <span className="text-sm font-semibold text-gray-700">
                    ₹{tier.priceRange?.min?.toLocaleString("en-IN")} — ₹{tier.priceRange?.max?.toLocaleString("en-IN")}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{tier.description}</p>
              </div>
            ))}
            {pricing.data.recommendation && (
              <p className="text-xs text-purple-600 bg-purple-50 rounded-lg p-2">{pricing.data.recommendation}</p>
            )}
          </div>
        )}

        {/* Generated / extracted package cards */}
        {currentPackages?.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              {currentPackages.map((pkg, i) => (
                <PackageCard key={i} pkg={pkg} />
              ))}
            </div>
            <button
              onClick={async () => {
                if (serviceId) {
                  try {
                    // First apply of a fresh set — seed the service pricing too.
                    const result = await dispatch(
                      applyPackagesToService({ serviceType, serviceId, packages: currentPackages, updatePricing: true })
                    ).unwrap();
                    setSavedPackages(result?.aiPackages || currentPackages);
                    toast.success("Packages saved to service");
                    onApplyPackages?.(currentPackages);
                  } catch (err) {
                    toast.error(err || "Failed to save packages");
                  }
                } else {
                  onApplyPackages?.(currentPackages);
                }
              }}
              className="w-full px-4 py-2 rounded-xl border-2 border-purple-500 text-purple-700 text-sm font-semibold hover:bg-purple-50 transition"
            >
              {serviceId ? "Save to Service" : "Apply to Listing"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
